import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { getLeaveBadgeEmoji } from '../../../../../../common/utils/leave-badge-emoji.js'
import type { LeaveBalancePayload } from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { Button } from '../../../../../components/button/button.js'
import { CalendarPlus } from '../../../../../components/icon/solar-icons.js'
import { openLeaveBooking } from '../../../../app-shell/leave-booking-opener.js'
import { formatDateRange } from '../../../utils/format.js'
import './leave-balance-card.css'

/** Slug of the one-shot "Töövaba nädal" perk — shown as used/not-used. */
const TOOVABA_NADAL_SLUG = 'toovaba-nadal'

/** Slug of the health-day leave type — shown as a plain remaining count. */
const TERVISEPAEV_SLUG = 'tervisepäev'

export const LeaveBalanceCard = ({ data, lang }: { data: LeaveBalancePayload; lang: Language }) => {
    const c = t.portal.dashboard.cards.leave_balance
    const { currentLeave, nextLeave, remainingDays, balanceAsOfTodayDays, specialDays } = data
    const isPending = nextLeave?.status === 'PENDING'

    return (
        <>
            {currentLeave && (
                <span
                    className="portal-card__subtext portal-leave-balance-card__current"
                    style={{ color: 'var(--portal-info)' }}
                >
                    {c.currentlyAway(lang)} · {getLeaveBadgeEmoji(currentLeave.badgeType)}{' '}
                    {formatDateRange(currentLeave.startDate, currentLeave.endDate)}
                </span>
            )}
            <span className="portal-card__stat-label">{c.nextLeave(lang)}</span>
            <span className={`portal-card__value${nextLeave ? '' : ' portal-card__value--empty'}`}>
                {nextLeave
                    ? formatDateRange(nextLeave.startDate, nextLeave.endDate)
                    : c.noUpcoming(lang)}
            </span>
            {nextLeave && (
                <span
                    className="portal-card__subtext"
                    style={isPending ? { color: 'var(--portal-warning)' } : undefined}
                >
                    {isPending
                        ? t.portal.timeOff.status.pending(lang)
                        : t.portal.timeOff.status.approved(lang)}
                </span>
            )}

            <hr className="portal-card__divider portal-card__divider--inner portal-card__divider--bottom" />

            <div className="portal-card__stats portal-card__stats--fill">
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label">{c.remaining(lang)}</span>
                    <span className="portal-card__stat-value">{c.days(remainingDays)(lang)}</span>
                </div>
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label">{c.balanceAsOfToday(lang)}</span>
                    <span className="portal-card__stat-value portal-card__stat-value--muted">
                        {c.days(balanceAsOfTodayDays)(lang)}
                    </span>
                </div>
            </div>

            {specialDays.length > 0 && (
                <div className="portal-leave-balance-card__special-days">
                    {specialDays.map((s) => {
                        // Töövaba nädal is a one-shot full week — show used/not-used,
                        // not a day count. Health days show only the remaining count
                        // (no "/max" capacity). Others show "left/max".
                        const isOneShotWeek = s.leaveTypeSlug === TOOVABA_NADAL_SLUG
                        const isHealthDay = s.leaveTypeSlug === TERVISEPAEV_SLUG
                        const value = isOneShotWeek
                            ? s.used > 0
                                ? c.specialUsed(lang)
                                : c.specialUnused(lang)
                            : isHealthDay
                              ? String(Math.round(s.available))
                              : c.specialFraction(
                                    Math.round(s.available),
                                    Math.round(s.total),
                                )(lang)
                        const label = isHealthDay ? c.healthDaysAvailable(lang) : s.leaveTypeName
                        return (
                            <div
                                key={s.policyId}
                                className="portal-leave-balance-card__special-day"
                            >
                                <span className="portal-card__stat-label">{label}</span>
                                <span className="portal-card__stat-value">{value}</span>
                            </div>
                        )
                    })}
                </div>
            )}

            <div className="portal-card__cta-row portal-card__cta-row--stacked">
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={CalendarPlus}
                    text={c.addLeave(lang)}
                    onClick={() => openLeaveBooking()}
                />
            </div>
        </>
    )
}
